import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiCheck, FiLoader, FiSearch, FiShoppingBag } from 'react-icons/fi';
import { cShops, fullUrl, getPlans } from '../../api';
import { useLanguage } from '../../i18n';
import { NAVY, ORANGE } from '../../components/community/brand';

/** All shops on the platform + the 3 plans shown to anyone who wants a shop. */
export default function ShopsPage() {
  const { t } = useLanguage();
  const [q, setQ] = useState('');
  const [shops, setShops] = useState([]);
  const [plans, setPlans] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = (term = '') => {
    setLoading(true);
    cShops({ q: term || undefined, limit: 60 })
      .then((res) => { setShops(res?.items || []); setTotal(res?.total || 0); })
      .catch(() => setShops([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    getPlans().then((rows) => setPlans(Array.isArray(rows) ? rows : [])).catch(() => setPlans([]));
  }, []);

  return (
    <div className="mx-auto max-w-[1100px] px-3 lg:px-4 py-5 space-y-6">
      {/* MiniShop focus — this is the 2-colour (blue + orange) part of the site:
          CodeKhmerLearning community is plain blue, MiniShop shops mix blue+orange. */}
      <section className="shops-hero rounded-2xl overflow-hidden text-white px-4 py-5 sm:px-6 shadow-sm">
        <div className="flex flex-wrap items-center gap-2">
          <FiShoppingBag className="w-5 h-5" style={{ color: '#FFD3A8' }} />
          <span className="text-[11px] font-extrabold px-2 py-0.5 rounded-md text-white" style={{ background: ORANGE }}>
            MiniShop
          </span>
          <span className="text-[11px] font-bold text-white/80">
            {t('communityBadge')} · CodeKhmerLearning
          </span>
        </div>

        <h1 className="mt-2 font-extrabold text-2xl leading-tight">{t('shopsDirectory')}</h1>
        <p className="text-xs sm:text-sm text-white/90 mt-1">{t('shopsFocusNote')}</p>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Link
            to="/create-shop"
            className="text-xs font-bold px-3.5 py-1.5 rounded-full text-white hover:brightness-110"
            style={{ background: ORANGE }}
          >
            🛍️ {t('createShopTitle')}
          </Link>
          <span className="text-[11px] text-white/80">{t('shopsDirectoryDesc')}</span>
        </div>
      </section>

      <form
        onSubmit={(e) => { e.preventDefault(); load(q.trim()); }}
        className="mx-auto max-w-xl flex items-center gap-2 rounded-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 px-4 py-2.5"
      >
        <FiSearch className="w-4 h-4 text-gray-400" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('searchShop')}
          className="flex-1 bg-transparent outline-none text-sm dark:text-gray-100"
        />
        <button type="submit" className="px-4 py-1.5 rounded-full text-white text-xs font-bold" style={{ background: ORANGE }}>
          {t('seeAll')}
        </button>
      </form>

      {/* 3 plans — shown before creating a shop */}
      <section className="rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
        <div className="text-center mb-4">
          <h2 className="font-extrabold text-lg">{t('createShopTitle')}</h2>
          <p className="text-xs text-gray-500">{t('createShopDesc')}</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          {plans.map((p, i) => {
            const featured = i === 1;
            return (
              <div
                key={p.id}
                className={`rounded-2xl p-4 border flex flex-col ${featured ? 'shadow-lg' : ''}`}
                style={featured ? { borderColor: ORANGE, background: `${ORANGE}0d` } : {}}
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-extrabold" style={{ color: NAVY }}>{p.name}</h3>
                  {p.free && (
                    <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full text-white" style={{ background: ORANGE }}>
                      FREE
                    </span>
                  )}
                </div>
                <p className="mt-2">
                  <span className="text-2xl font-extrabold">${p.price}</span>
                  <span className="text-xs text-gray-500 ml-1">
                    / {p.days >= 365 ? '1y' : p.days >= 60 ? `${Math.round(p.days / 30)}m` : '1m'}
                  </span>
                </p>
                <ul className="mt-3 space-y-1.5 text-xs text-gray-600 dark:text-gray-300 flex-1">
                  <li className="flex items-center gap-1.5">
                    <FiCheck className="w-3.5 h-3.5" style={{ color: ORANGE }} /> {p.max_products} {t('products')}
                  </li>
                  <li className="flex items-center gap-1.5">
                    <FiCheck className="w-3.5 h-3.5" style={{ color: ORANGE }} /> {p.max_categories} {t('categories')}
                  </li>
                  <li className="flex items-center gap-1.5">
                    <FiCheck className="w-3.5 h-3.5" style={{ color: ORANGE }} /> ABA Pay · POS · Telegram
                  </li>
                </ul>
                <Link
                  to="/create-shop"
                  className="mt-4 inline-flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-white text-sm font-bold"
                  style={{ background: featured ? ORANGE : NAVY }}
                >
                  {t('choosePlan')} <FiArrowRight className="w-4 h-4" />
                </Link>
              </div>
            );
          })}
          {plans.length === 0 && <p className="text-xs text-gray-400">{t('loading')}</p>}
        </div>
      </section>

      {/* Shops list */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-extrabold text-lg inline-flex items-center gap-2">
            <FiShoppingBag style={{ color: ORANGE }} /> {t('shopsDirectory')}
          </h2>
          <span className="text-xs text-gray-500">{total}</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-12 text-gray-500">
            <FiLoader className="w-5 h-5 animate-spin" /> {t('loading')}
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {shops.map((s) => (
              <div key={s.id} className="rounded-2xl border border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 flex gap-3 card-hover">
                {s.logo ? (
                  <img src={fullUrl(s.logo)} alt={s.shop_name} className="w-14 h-14 rounded-xl object-cover shrink-0" />
                ) : (
                  <span className="w-14 h-14 rounded-xl flex items-center justify-center text-white font-extrabold shrink-0" style={{ background: NAVY }}>
                    {(s.shop_name || s.username).slice(0, 2).toUpperCase()}
                  </span>
                )}
                <div className="min-w-0 flex-1 flex flex-col">
                  <p className="font-bold text-sm truncate">{s.shop_name}</p>
                  <p className="text-[11px] text-gray-400">@{s.username}</p>
                  <p className="text-xs text-gray-500 line-clamp-2 mt-1">{s.bio}</p>
                  <div className="mt-auto pt-2 flex items-center justify-between">
                    <span className="text-[11px] text-gray-400">{s.product_count} {t('productsCount')}</span>
                    <Link
                      to={`/${s.username}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-white text-xs font-bold"
                      style={{ background: ORANGE }}
                    >
                      {t('viewShop')} <FiArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
            {shops.length === 0 && <p className="text-sm text-gray-400">{t('noProducts')}</p>}
          </div>
        )}
      </section>
    </div>
  );
}
